import React, { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import StarIcon from '@mui/icons-material/Star'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HelpIcon from '@mui/icons-material/Help'
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined'

import DashboardPanel, { DashboardPanelEmpty } from '../DashboardPanel'
import { useAppSelector } from '../../hooks'
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions'
import { isOrganiserOrActiveInvitee, readMyUpcomingBookings } from '../../services/myBookingsRead'
import type { RoomBookingData } from '../../../../types/RoomBooking'
import { formatDateHeader, formatTimeRange } from './bookingCardUtils'

/**
 * Meetings the user organises or is invited to that haven't ended, soonest first. Cancelled ones
 * and ones the user declined are left out; the two cloud lists overlap, so they are de-duplicated.
 */
export function selectUpcomingMeetings(
  mine: RoomBookingData[],
  invited: RoomBookingData[],
  userId: string,
  now: Date = new Date()
): RoomBookingData[] {
  const byId = new Map<string, RoomBookingData>()
  for (const b of [...mine, ...invited]) {
    if (!b.objectId || byId.has(b.objectId)) continue
    if (b.status === 'CANCELLED' || new Date(b.endTime) <= now) continue
    if (!isOrganiserOrActiveInvitee(b, userId)) continue
    byId.set(b.objectId, b)
  }
  return [...byId.values()].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
}

/** "Today · 14:00 – 15:00 CEST" */
export function formatMeetingWhen(booking: RoomBookingData): string {
  return `${formatDateHeader(booking.startTime)} · ${formatTimeRange(booking.startTime, booking.endTime)}`
}

function RsvpIcon({ booking, userId }: { booking: RoomBookingData; userId: string }) {
  if (booking.creatorId === userId) {
    return (
      <Tooltip title="You're organising this">
        <StarIcon data-testid="rsvp-organiser" fontSize="small" sx={{ color: 'primary.main' }} />
      </Tooltip>
    )
  }
  const status = booking.invitees.find((inv) => inv.userId === userId)?.status
  if (status === 'accepted') {
    return (
      <Tooltip title="Going">
        <CheckCircleIcon data-testid="rsvp-accepted" fontSize="small" sx={{ color: 'success.main' }} />
      </Tooltip>
    )
  }
  if (status === 'maybe') {
    return (
      <Tooltip title="Maybe">
        <HelpIcon data-testid="rsvp-maybe" fontSize="small" sx={{ color: 'warning.main' }} />
      </Tooltip>
    )
  }
  return (
    <Tooltip title="Not answered yet">
      <PendingOutlinedIcon data-testid="rsvp-pending" fontSize="small" sx={{ color: 'text.secondary' }} />
    </Tooltip>
  )
}

export interface UpcomingMeetingsListProps {
  /** Room names by customRoomId: a VR is shared by every room on its map, so its name needs the room's. */
  roomNames: Record<string, string>
  onViewAll: () => void
}

/** The Dashboard's upcoming meetings (SC-422). Read-only; RSVP and edits stay on the Meetings page. */
export default function UpcomingMeetingsList({ roomNames, onViewAll }: UpcomingMeetingsListProps) {
  const { currentCompany } = useCompanyPermissions()
  const companyId = currentCompany?.id || ''
  const userId = useAppSelector((s) => s.user.userData?.id) || ''
  const bookingsRefreshTrigger = useAppSelector((s) => s.roomBooking.bookingsRefreshTrigger)
  const [meetings, setMeetings] = useState<RoomBookingData[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    setLoading(true)
    // Kept local: the Meetings page's Upcoming/Today/Past filter owns the shared store lists.
    // One call: the "created or invited" list already holds what the old second call (invited
    // only) returned, and it is shared with the reminders hook, which asks at the same time.
    readMyUpcomingBookings(companyId, bookingsRefreshTrigger)
      .then((bookings) => {
        if (!cancelled) setMeetings(selectUpcomingMeetings(bookings, [], userId))
      })
      .catch((err) => console.warn('[UpcomingMeetingsList] fetch failed:', err))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [companyId, userId, bookingsRefreshTrigger])

  return (
    <DashboardPanel
      title="Upcoming meetings"
      data-testid="upcoming-meetings"
      action={
        <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none' }}>
          View all meetings
        </Button>
      }
    >
      {loading && meetings.length === 0 ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : meetings.length === 0 ? (
        <DashboardPanelEmpty>No upcoming meetings</DashboardPanelEmpty>
      ) : (
        meetings.map((booking) => {
          const roomName = roomNames[booking.customRoomId]
          const where = [booking.roomName, roomName].filter(Boolean).join(' · ')
          return (
            <Box
              key={booking.objectId}
              data-testid="upcoming-meeting-row"
              sx={(t) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
                '&:hover': { backgroundColor: alpha(t.palette.primary.main, 0.04) },
              })}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={booking.title} sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {booking.title}
                </Typography>
                <Typography variant="caption" noWrap component="div" sx={{ color: 'text.secondary' }}>
                  {formatMeetingWhen(booking)}
                </Typography>
                {where && (
                  <Typography variant="caption" noWrap component="div" title={where} sx={{ color: 'text.secondary' }}>
                    {where}
                  </Typography>
                )}
              </Box>
              <RsvpIcon booking={booking} userId={userId} />
            </Box>
          )
        })
      )}
    </DashboardPanel>
  )
}
